import { supabase } from "@/integrations/supabase/client";

export type Coords = { lat: number; lon: number };

// Haversine distance in km
export const distanceKm = (a: Coords, b: Coords) => {
  const R = 6371;
  const toRad = (x: number) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.sin(dLon / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(h));
};

// In-memory cache for the current page lifetime
const memCache = new Map<string, Coords | null>();
let geoCachePromise: Promise<Record<string, Coords>> | null = null;

const loadServerCache = async (): Promise<Record<string, Coords>> => {
  if (geoCachePromise) return geoCachePromise;
  geoCachePromise = (async () => {
    const { data } = await supabase.from("site_settings").select("value").eq("key", "geo_cache").maybeSingle();
    const v = (data as any)?.value;
    return (v && typeof v === "object" && !Array.isArray(v)) ? v as Record<string, Coords> : {};
  })();
  return geoCachePromise;
};

const persistToServerCache = async (key: string, coords: Coords) => {
  try {
    const current = await loadServerCache();
    if (current[key]) return;
    const next = { ...current, [key]: coords };
    geoCachePromise = Promise.resolve(next);
    // RPC bypasses RLS so anonymous/authenticated users can contribute to the shared cache.
    await (supabase as any).rpc("upsert_geo_cache", { _key: key, _lat: coords.lat, _lon: coords.lon });
  } catch {
    /* ignore */
  }
};

const normalize = (s: string) => s.trim().toLowerCase();

export const geocodePostalCode = async (cp: string): Promise<Coords | null> => {
  const code = (cp || "").trim();
  if (!/^\d{5}$/.test(code)) return null;
  const key = `cp:${code}`;
  if (memCache.has(key)) return memCache.get(key)!;
  const server = await loadServerCache();
  if (server[key]) { memCache.set(key, server[key]); return server[key]; }
  try {
    const res = await fetch(`https://api.zippopotam.us/es/${code}`);
    if (!res.ok) { memCache.set(key, null); return null; }
    const j = await res.json();
    const place = j?.places?.[0];
    if (!place) { memCache.set(key, null); return null; }
    const coords: Coords = { lat: parseFloat(place.latitude), lon: parseFloat(place.longitude) };
    memCache.set(key, coords);
    void persistToServerCache(key, coords);
    return coords;
  } catch {
    memCache.set(key, null);
    return null;
  }
};

export const geocodeCity = async (city: string): Promise<Coords | null> => {
  const name = (city || "").trim();
  if (!name) return null;
  const key = `city:${normalize(name)}`;
  if (memCache.has(key)) return memCache.get(key)!;
  const server = await loadServerCache();
  if (server[key]) { memCache.set(key, server[key]); return server[key]; }
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?country=spain&city=${encodeURIComponent(name)}&format=json&limit=1`
    );
    if (!res.ok) { memCache.set(key, null); return null; }
    const arr = await res.json();
    if (!Array.isArray(arr) || !arr[0]) { memCache.set(key, null); return null; }
    const coords: Coords = { lat: parseFloat(arr[0].lat), lon: parseFloat(arr[0].lon) };
    memCache.set(key, coords);
    void persistToServerCache(key, coords);
    return coords;
  } catch {
    memCache.set(key, null);
    return null;
  }
};
